import * as React from 'react';
import cn from 'classnames';

interface AnimatedMountProps {
  children: React.ReactNode;
  show?: boolean;
  className?: string;
  duration?: 'fast' | 'normal' | 'slow';
}

export const AnimatedMount: React.FC<AnimatedMountProps> = ({
  children,
  show,
  className,
  duration = 'normal',
}) => {
  const [mounted, setMounted] = React.useState(false);
  const [childHeight, setChildHeight] = React.useState<number | null>(null);
  const childRef = React.useRef<HTMLDivElement | null>(null);

  const durationMap = {
    fast: { ms: 200, className: 'rc-duration-200' },
    normal: { ms: 300, className: 'rc-duration-300' },
    slow: { ms: 500, className: 'rc-duration-500' },
  };

  const timeoutRef = React.useRef<NodeJS.Timeout | null>(null);

  React.useEffect(() => {
    if (show) {
      timeoutRef.current && clearTimeout(timeoutRef.current);
      setMounted(true);
    } else {
      timeoutRef.current = setTimeout(
        () => setMounted(false),
        durationMap[duration].ms
      );
    }

    return () => {
      timeoutRef.current && clearTimeout(timeoutRef.current);
    };
  }, [show, duration]);

  React.useEffect(() => {
    if (childRef.current) {
      setChildHeight(childRef.current.scrollHeight);
    }
  }, [children]);

  if (!mounted && !show) {
    return null;
  }

  return (
    <div
      className={cn(
        'rc-transition-all',
        'motion-safe:rc-transition-all',
        durationMap[duration].className,
        show ? 'rc-motion-opacity-in-0' : 'rc-motion-opacity-out-0',
        show
          ? 'rc-motion-translate-y-in-[20px]'
          : '-rc-motion-translate-y-out-[20px]',
        className
      )}
      style={{
        maxHeight: show ? `${childHeight}px` : '0',
        overflow: 'hidden',
      }}
    >
      <div ref={childRef}>{children}</div>
    </div>
  );
};
